import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { MessageSquare, MessageCircle } from "lucide-react";
import { buildMessage, buildSmsLink, buildWhatsappLink } from "../lib/notifyClient";
import { logError } from "../lib/logger";

const EVENT_OPTIONS = [
  { id: "confirmation", label: "Confirmação de agendamento" },
  { id: "reminder", label: "Lembrete 1 dia antes" },
  { id: "started", label: "Equipe chegou" },
  { id: "finished", label: "Limpeza concluída" },
];

export function NotifyClientButton({ client, job, team, testId = "notify-client-btn" }) {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [eventKey, setEventKey] = useState("reminder");
  const [custom, setCustom] = useState("");

  useEffect(() => {
    if (!open || settings) return;
    api
      .get("/notification-settings")
      .then((r) => {
        setSettings(r.data);
        // Default to "finished" if job is finished, else "reminder"
        const def = job?.status === "finalizada" ? "finished" : "reminder";
        setEventKey(def);
      })
      .catch((e) => logError(e));
  }, [open, settings, job?.status]);

  const template = settings?.[eventKey]?.template || "";
  const message = custom || buildMessage(template, { client, job, team });
  const phone = client?.phone || "";
  const hasPhone = phone && phone.replace(/\D/g, "").length >= 10;

  const wa = hasPhone ? buildWhatsappLink(phone, message) : null;
  const sms = hasPhone ? buildSmsLink(phone, message) : null;

  async function logSent(channel) {
    try {
      await api.post("/notification-history", {
        client_id: client.id,
        job_id: job?.id,
        event_key: eventKey,
        channel,
        message,
      });
    } catch (e) {
      logError("Failed to log notification:", e);
    }
  }

  if (!client) return null;

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        data-testid={testId}
        className="border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
      >
        <MessageSquare size={14} className="mr-1" /> Notificar cliente
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Notificar {client.name}</DialogTitle>
            <DialogDescription>
              Envie WhatsApp ou SMS com uma mensagem pré-formatada baseada nos
              templates de notificação.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <label className="block">
              <span className="text-xs font-medium text-slate-600 mb-1 block">
                Tipo de evento
              </span>
              <select
                className="cm-input"
                value={eventKey}
                onChange={(e) => {
                  setEventKey(e.target.value);
                  setCustom("");
                }}
                data-testid="notify-event-select"
              >
                {EVENT_OPTIONS.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="text-xs font-medium text-slate-600 mb-1 block">
                Mensagem (editável)
              </span>
              <textarea
                rows={5}
                className="cm-input text-sm"
                value={custom || message}
                onChange={(e) => setCustom(e.target.value)}
                data-testid="notify-message-text"
              />
            </label>

            <div className="text-xs text-slate-500">
              Telefone:{" "}
              {hasPhone ? (
                <strong>{phone}</strong>
              ) : (
                <span className="text-amber-600">
                  Cliente sem telefone cadastrado
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 mt-2">
            <a
              href={wa || "#"}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => {
                if (!hasPhone) {
                  e.preventDefault();
                  return;
                }
                logSent("whatsapp");
                setOpen(false);
              }}
              data-testid="notify-send-whatsapp"
              className={`flex-1 inline-flex items-center justify-center gap-1 px-4 py-2.5 rounded-lg font-semibold text-white transition-colors ${
                hasPhone
                  ? "bg-emerald-600 hover:bg-emerald-700"
                  : "bg-slate-300 cursor-not-allowed pointer-events-none"
              }`}
            >
              <MessageCircle size={16} /> Enviar WhatsApp
            </a>
            <a
              href={sms || "#"}
              onClick={(e) => {
                if (!hasPhone) {
                  e.preventDefault();
                  return;
                }
                setOpen(false);
              }}
              data-testid="notify-send-sms"
              className={`flex-1 inline-flex items-center justify-center gap-1 px-4 py-2.5 rounded-lg font-semibold transition-colors border ${
                hasPhone
                  ? "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                  : "bg-slate-50 text-slate-300 border-slate-200 cursor-not-allowed pointer-events-none"
              }`}
            >
              <MessageSquare size={16} /> Enviar SMS
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
